import type {
  IncomingFluencyResponse,
  IncomingLearningRecord,
} from "@bindery/convex-reader/submission";
import { randomUUID } from "@bindery/logging/random-uuid";
import {
  appendResponse,
  type ResponseId,
} from "@bindery/shared/contracts/fluency";
import {
  readMeta,
  readerRange,
  writeMeta,
  type FluencyRow,
  type OutboxRecordRow,
  type OutboxResponseRow,
  type QuarantineKind,
  type ReaderDb,
  type RecordState,
} from "@/storage/db";
import {
  convexErrorKind,
  type SubmitResult,
  type SyncBackend,
} from "./backend";
import { withServerHistory } from "./fluency-history";
import {
  chunkForSubmit,
  halve,
  rowCount,
  splitForIsolation,
  type Chunk,
} from "./chunk";
import {
  preQueueRecord,
  preQueueResponse,
  toWireRecord,
  toWireResponse,
  type QueueRejection,
} from "./pre-queue";
import {
  createRetryGate,
  systemClock,
  type Clock,
  type RetryGate,
  type SyncLogger,
} from "./retry";

// Q2 (Russ, 2026-10-04): a row waits this long for its reader to have a
// session on the device again, then is dropped unsent.
export const QUEUED_ROW_RETENTION_MS = 30 * 24 * 60 * 60_000;

export const readerSessionKey = (readerId: string): string =>
  `readerSessionAt:${readerId}`;

export const QUEUE_ORDER_STEP_MS = 0.001;

export const QUARANTINE_RETENTION_MS = 30 * 24 * 60 * 60_000;

// About 8 min of backoff: past this a chunk the server keeps refusing is
// split to find the row it refuses, so one bad row cannot hold the queue.
export const ISOLATE_AFTER_ATTEMPTS = 8;

// About 13 min of backoff. An error that is not an argument refusal is
// never quarantined, so a permanent one would back off unseen forever.
export const STUCK_AFTER_FAILURES = 10;

// A held tap's reader, persisted past its provisional's acknowledgement and
// a reload, so the final copy is filed under the reader who tapped.
interface HeldTap {
  readerId: string;
  heldAt: number;
}
const HELD_TAP_PREFIX = "heldTap:";
const heldTapKey = (clientRecordId: string): string =>
  `${HELD_TAP_PREFIX}${clientRecordId}`;
// A held tap ends within a reading; an entry this old was never finalized.
export const HELD_TAP_RETENTION_MS = 24 * 60 * 60_000;

// One name across every tab of the origin: navigator.locks serializes
// drains between tabs sharing this Dexie.
export const OUTBOX_LOCK_NAME = "rbe-reader-outbox";

export type OutboxLocks = Pick<LockManager, "request">;

const browserLocks = (): OutboxLocks | null =>
  typeof navigator === "undefined"
    ? null
    : // oxlint-disable-next-line compat/compat -- absent on Safari 14, which falls back to per-tab drains
      (navigator.locks ?? null);

export type FluencyResponseInput = Omit<
  IncomingFluencyResponse,
  "clientResponseId"
> & { clientResponseId?: string };

export interface OutboxDeps {
  db: ReaderDb;
  backend: SyncBackend;
  currentReaderId: () => string | null;
  /** True when the error was an unauthenticated refusal (`signedOutBy`). */
  signedOut: (error: unknown) => boolean;
  logger: SyncLogger;
  clock?: Clock;
  newId?: () => string;
  /** Called once after a flush that had at least one chunk acknowledged. */
  onAcknowledged?: () => void;
  /**
   * After a flush, the words whose stored history may now be wrong: a
   * duplicate acknowledgement means an earlier, failed call had committed
   * and a merge since may have counted those responses twice; a refused
   * response was replayed into its word's history but never applied.
   */
  onHistoriesInDoubt?: (readerId: string, words: string[]) => void;
  /** The token named another reader than the rows': re-read the session. */
  onReaderMismatch?: () => void;
  /** Defaults to navigator.locks; with none (Safari 14), drains are per tab. */
  locks?: OutboxLocks | null;
}

// Bumped in Dexie when any tab's drain with something to send starts and
// when it ends. A server history fetched across a bump may or may not
// hold that drain's responses, and replaying the outbox onto it would
// count them twice or lose them; the merge compares it inside its own
// transaction, so this works across tabs with or without Web Locks.
const SUBMIT_EPOCH_KEY = "outboxSubmitEpoch";

export const readSubmitEpoch = async (db: ReaderDb): Promise<number> =>
  (await readMeta<number>(db, SUBMIT_EPOCH_KEY)) ?? 0;

const bumpSubmitEpoch = (db: ReaderDb): Promise<void> =>
  db.transaction("rw", db.meta, async () => {
    await writeMeta(db, SUBMIT_EPOCH_KEY, (await readSubmitEpoch(db)) + 1);
  });

/**
 * Each write takes the reader whose rows it files, when its caller knows
 * it. A write queued before a reader switch and run after it would
 * otherwise take the session's new reader; filed under its own, it waits
 * for that reader's token like any other row.
 */
export interface Outbox {
  /** Queues a record with its linked responses in one transaction. */
  queueRecord: (
    record: IncomingLearningRecord,
    responses?: readonly FluencyResponseInput[],
    readerId?: string,
  ) => Promise<FluencyRow[]>;
  holdTapRecord: (
    provisional: IncomingLearningRecord,
    responses?: readonly FluencyResponseInput[],
    readerId?: string,
  ) => Promise<FluencyRow[]>;
  finalizeHeldTapRecord: (
    final: IncomingLearningRecord,
    readerId?: string,
  ) => Promise<void>;
  /** Queues responses and replays them onto each word's stored history. */
  queueFluencyResponses: (
    responses: readonly FluencyResponseInput[],
    readerId?: string,
  ) => Promise<FluencyRow[]>;
  flush: () => Promise<void>;
  /** Clears a backoff or a signed-out pause, then flushes. */
  retryNow: () => Promise<void>;
  flushIfOlderThan: (ageMs: number) => Promise<void>;
  /** Records that `readerId` has a session on this device now. */
  noteReaderSession: (readerId: string) => Promise<void>;
  /**
   * Drops the rows of every reader but the current one that have waited
   * QUEUED_ROW_RETENTION_MS without a session for their reader. Does
   * nothing while no reader is signed in: the one about to be might be
   * the rows' reader.
   */
  expireQueuedRows: () => Promise<void>;
  /** Resolves when no flush is running; starts none. */
  whenIdle: () => Promise<void>;
}

type Sent = { status: "acked"; skipped: number } | { status: "stopped" };
type Attempt = { result: SubmitResult } | { error: unknown };

const STOPPED: Sent = { status: "stopped" };

export function createOutbox({
  db,
  backend,
  currentReaderId,
  signedOut,
  logger,
  clock = systemClock,
  newId = randomUUID,
  onAcknowledged,
  onHistoriesInDoubt,
  onReaderMismatch,
  locks = browserLocks(),
}: OutboxDeps): Outbox {
  const retry: RetryGate = createRetryGate(clock);
  // IndexedDB orders equal [readerId+queuedAt] keys by the random primary
  // key, so rows queued in one millisecond would come back shuffled; each
  // row gets its own queuedAt, a thousandth of a millisecond apart.
  let lastQueuedAt = Number.NEGATIVE_INFINITY;
  const nextQueuedAt = (): number => {
    lastQueuedAt = Math.max(clock.now(), lastQueuedAt + QUEUE_ORDER_STEP_MS);
    return lastQueuedAt;
  };
  let paused = false;
  let inFlight: Promise<void> | null = null;
  let flushAgain = false;
  let failureStreak = { signature: "", count: 0 };
  let wordsInDoubt = new Set<string>();

  function requireReader(
    stage: string,
    pinned: string | undefined,
  ): string | null {
    const readerId = pinned ?? currentReaderId();
    if (readerId === null) {
      logger.error(
        "sync.submit.error",
        { type: "NoReader", message: "queued without a reader session" },
        { stage },
      );
    }
    return readerId;
  }

  async function quarantine(
    readerId: string,
    kind: QuarantineKind,
    payload: unknown,
    rejection: QueueRejection,
  ): Promise<void> {
    await db.quarantine.add({
      readerId,
      kind,
      payload,
      field: rejection.field,
      reason: rejection.reason,
      quarantinedAt: clock.now(),
    });
  }

  async function quarantineBeforeQueue(
    readerId: string,
    kind: QuarantineKind,
    payload: unknown,
    rejection: QueueRejection,
  ): Promise<void> {
    await quarantine(readerId, kind, payload, rejection);
    logger.error(
      "sync.outbox.quarantine",
      { type: "PreQueueRejected", message: rejection.field },
      { kind, field: rejection.field, reason: rejection.reason },
    );
  }

  async function checkResponses(
    readerId: string,
    responses: readonly FluencyResponseInput[],
  ): Promise<OutboxResponseRow[]> {
    const now = clock.now();
    const accepted: OutboxResponseRow[] = [];
    for (const input of responses) {
      const response = {
        ...input,
        clientResponseId: input.clientResponseId ?? newId(),
      };
      const checked = preQueueResponse(response, readerId, now);
      if (checked.kind === "drop") continue;
      if (checked.kind === "quarantine") {
        await quarantineBeforeQueue(
          readerId,
          "response",
          response,
          checked.rejection,
        );
        continue;
      }
      accepted.push({
        ...checked.value,
        readerId,
        queuedAt: nextQueuedAt(),
        attempts: 0,
      });
    }
    return accepted;
  }

  // Runs inside a transaction over outboxResponses and fluency.
  async function writeResponses(
    readerId: string,
    accepted: readonly OutboxResponseRow[],
  ): Promise<FluencyRow[]> {
    if (accepted.length === 0) return [];
    await db.outboxResponses.bulkPut([...accepted]);
    const touched = new Map<string, FluencyRow>();
    for (const row of accepted) {
      const stored =
        touched.get(row.word) ?? (await db.fluency.get([readerId, row.word]));
      touched.set(row.word, {
        readerId,
        word: row.word,
        serverHistory: stored?.serverHistory ?? [],
        history: appendResponse(
          stored?.history ?? [],
          row.response as ResponseId,
        ),
        lastUpdated: stored?.lastUpdated ?? 0,
        ...(stored?.serverMerged && { serverMerged: true }),
      });
    }
    const rows = [...touched.values()];
    await db.fluency.bulkPut(rows);
    return rows;
  }

  async function putRecord(
    record: IncomingLearningRecord,
    state: RecordState,
    responses: readonly FluencyResponseInput[],
    pinned: string | undefined,
  ): Promise<FluencyRow[]> {
    const current = requireReader("queue_record", pinned);
    if (current === null) return [];
    const heldKey = heldTapKey(record.clientRecordId);
    const held = await readMeta<HeldTap>(db, heldKey);
    const readerId = held?.readerId ?? current;
    const checked = preQueueRecord(record, readerId, clock.now());
    if (checked.kind === "quarantine") {
      await quarantineBeforeQueue(
        readerId,
        "record",
        record,
        checked.rejection,
      );
    }
    const accepted = await checkResponses(readerId, responses);
    const value = checked.kind === "queue" ? checked.value : null;
    return db.transaction(
      "rw",
      db.outboxRecords,
      db.outboxResponses,
      db.fluency,
      db.meta,
      async () => {
        if (state === "provisional" && held === undefined) {
          await writeMeta(db, heldKey, {
            readerId,
            heldAt: clock.now(),
          } satisfies HeldTap);
        } else if (state === "pending") {
          await db.meta.delete(heldKey);
        }
        if (value) {
          const existing = await db.outboxRecords.get(value.clientRecordId);
          const regresses =
            existing?.state === "pending" && state === "provisional";
          if (!regresses) {
            await db.outboxRecords.put({
              ...value,
              readerId: existing?.readerId ?? readerId,
              queuedAt: existing?.queuedAt ?? nextQueuedAt(),
              attempts: existing?.attempts ?? 0,
              state,
            });
          }
        }
        return writeResponses(readerId, accepted);
      },
    );
  }

  async function queueFluencyResponses(
    responses: readonly FluencyResponseInput[],
    pinned?: string,
  ): Promise<FluencyRow[]> {
    if (responses.length === 0) return [];
    const readerId = requireReader("queue_responses", pinned);
    if (readerId === null) return [];
    const accepted = await checkResponses(readerId, responses);
    return db.transaction("rw", db.outboxResponses, db.fluency, () =>
      writeResponses(readerId, accepted),
    );
  }

  async function pendingFor(readerId: string): Promise<Chunk> {
    const [lower, upper] = readerRange(readerId);
    return db.transaction(
      "r",
      db.outboxRecords,
      db.outboxResponses,
      async () => ({
        records: await db.outboxRecords
          .where("[readerId+queuedAt]")
          .between(lower, upper)
          .toArray(),
        responses: await db.outboxResponses
          .where("[readerId+queuedAt]")
          .between(lower, upper)
          .toArray(),
      }),
    );
  }

  // A final copy queued while its provisional was in flight stays, and is
  // re-sent for the server to upgrade; only the copy that was sent goes.
  async function removeSentRecord(sent: OutboxRecordRow): Promise<void> {
    const current = await db.outboxRecords.get(sent.clientRecordId);
    if (current?.state === sent.state) {
      await db.outboxRecords.delete(sent.clientRecordId);
    }
  }

  async function applyResult(
    readerId: string,
    chunk: Chunk,
    result: SubmitResult,
  ): Promise<number> {
    const rejectedRecords = new Map(
      result.learningRecords.rejected.map((entry) => [entry.index, entry]),
    );
    const rejectedResponses = new Map(
      result.fluencyResponses.rejected.map((entry) => [entry.index, entry]),
    );
    const committedEarlier =
      result.fluencyResponses.duplicates > 0 ||
      chunk.responses.some((row) => row.attempts > 0);
    if (committedEarlier) {
      for (const row of chunk.responses) wordsInDoubt.add(row.word);
    }
    await db.transaction(
      "rw",
      db.outboxRecords,
      db.outboxResponses,
      db.quarantine,
      async () => {
        for (const [index, sent] of chunk.records.entries()) {
          const rejection = rejectedRecords.get(index);
          if (rejection) {
            await quarantine(readerId, "record", toWireRecord(sent), rejection);
          }
          await removeSentRecord(sent);
        }
        for (const [index, sent] of chunk.responses.entries()) {
          const rejection = rejectedResponses.get(index);
          if (rejection) {
            wordsInDoubt.add(sent.word);
            await quarantine(
              readerId,
              "response",
              toWireResponse(sent),
              rejection,
            );
          }
          await db.outboxResponses.delete(sent.clientResponseId);
        }
      },
    );
    for (const [kind, rejected] of [
      ["record", result.learningRecords.rejected],
      ["response", result.fluencyResponses.rejected],
    ] as const) {
      for (const { field, reason } of rejected) {
        logger.error(
          "sync.submit.rejected",
          { type: "SubmitRejected", message: field, code: reason },
          { kind, field, reason },
        );
      }
    }
    for (const divergence of result.actionIdDivergences) {
      logger.event("sync.action_id.divergence", { ...divergence });
    }
    for (const divergence of result.assignmentDivergences) {
      logger.event("sync.assignment.divergence", { ...divergence });
    }
    return (
      result.learningRecords.skipped.length +
      result.fluencyResponses.skipped.length
    );
  }

  /** The chunk's highest attempt count after this one. */
  async function countAttempt(chunk: Chunk): Promise<number> {
    await db.transaction(
      "rw",
      db.outboxRecords,
      db.outboxResponses,
      async () => {
        await db.outboxRecords
          .where("clientRecordId")
          .anyOf(chunk.records.map((row) => row.clientRecordId))
          .modify((row) => {
            row.attempts += 1;
          });
        await db.outboxResponses
          .where("clientResponseId")
          .anyOf(chunk.responses.map((row) => row.clientResponseId))
          .modify((row) => {
            row.attempts += 1;
          });
      },
    );
    return (
      Math.max(
        ...chunk.records.map((row) => row.attempts),
        ...chunk.responses.map((row) => row.attempts),
      ) + 1
    );
  }

  async function attempt(readerId: string, chunk: Chunk): Promise<Attempt> {
    try {
      return {
        result: await backend.submit(readerId, {
          learningRecords: chunk.records.map(toWireRecord),
          fluencyResponses: chunk.responses.map(toWireResponse),
        }),
      };
    } catch (error) {
      return { error };
    }
  }

  function backOff(chunk: Chunk, error: unknown, outcome: string): void {
    const delay = retry.failed(() => void flush());
    logger.error("sync.submit.error", error, {
      records: chunk.records.length,
      responses: chunk.responses.length,
      outcome,
      retry_in_ms: delay,
    });
    noteFailure(chunk, error);
  }

  function succeeded(): void {
    retry.succeeded();
    failureStreak = { signature: "", count: 0 };
  }

  function noteFailure(chunk: Chunk, error: unknown): void {
    const signature = `${chunkKey(chunk)}\n${errorMessage(error)}`;
    failureStreak =
      failureStreak.signature === signature
        ? { signature, count: failureStreak.count + 1 }
        : { signature, count: 1 };
    if (failureStreak.count === STUCK_AFTER_FAILURES) {
      logger.error("sync.outbox.stuck", error, {
        failures: failureStreak.count,
        records: chunk.records.length,
        responses: chunk.responses.length,
      });
    }
  }

  // Errors that say nothing about the rows: every row is kept and the
  // flush stops. A reader mismatch also re-reads the session and retries on
  // the backoff, by which time Convex usually holds the right token.
  function stopsFlush(chunk: Chunk, error: unknown): boolean {
    if (signedOut(error)) {
      paused = true;
      return true;
    }
    if (convexErrorKind(error) === "reader_mismatch") {
      onReaderMismatch?.();
      backOff(chunk, error, "reader_mismatch");
      return true;
    }
    return false;
  }

  async function send(
    readerId: string,
    chunk: Chunk,
    mayHalve: boolean,
  ): Promise<Sent> {
    const outcome = await attempt(readerId, chunk);
    if ("result" in outcome) {
      succeeded();
      return {
        status: "acked",
        skipped: await applyResult(readerId, chunk, outcome.result),
      };
    }
    const { error } = outcome;
    if (stopsFlush(chunk, error)) return STOPPED;
    if (mayHalve && convexErrorKind(error) === "invalid_batch") {
      logger.error("sync.submit.error", error, {
        records: chunk.records.length,
        responses: chunk.responses.length,
        outcome: "halved",
      });
      let skipped = 0;
      for (const half of halve(chunk)) {
        const sent = await send(readerId, half, false);
        if (sent.status === "stopped") return sent;
        skipped += sent.skipped;
      }
      return { status: "acked", skipped };
    }
    const attempts = await countAttempt(chunk);
    if (attempts >= ISOLATE_AFTER_ATTEMPTS && refusesTheRows(error)) {
      return isolate(readerId, chunk, error);
    }
    backOff(chunk, error, "backoff");
    return STOPPED;
  }

  async function quarantineRefused(
    readerId: string,
    chunk: Chunk,
    error: unknown,
  ): Promise<void> {
    const rejection: QueueRejection = {
      field: "(row)",
      reason: "server_refused",
    };
    await db.transaction(
      "rw",
      db.outboxRecords,
      db.outboxResponses,
      db.quarantine,
      async () => {
        for (const sent of chunk.records) {
          await quarantine(readerId, "record", toWireRecord(sent), rejection);
          await removeSentRecord(sent);
        }
        for (const sent of chunk.responses) {
          wordsInDoubt.add(sent.word);
          await quarantine(
            readerId,
            "response",
            toWireResponse(sent),
            rejection,
          );
          await db.outboxResponses.delete(sent.clientResponseId);
        }
      },
    );
    logger.error("sync.outbox.quarantine", error, {
      kind: chunk.records.length > 0 ? "record" : "response",
      reason: "server_refused",
    });
  }

  // Splits a refused chunk until each refused row stands alone. A row is
  // quarantined only once the server has accepted another part of the
  // chunk: if nothing is accepted the fault is the server's, not a row's,
  // and the whole chunk backs off instead.
  async function isolate(
    readerId: string,
    chunk: Chunk,
    error: unknown,
  ): Promise<Sent> {
    const progress = { accepted: false, skipped: 0 };

    async function probe(part: Chunk): Promise<"done" | "stopped" | "server"> {
      if (rowCount(part) === 1) {
        if (!progress.accepted) return "server";
        await quarantineRefused(readerId, part, error);
        return "done";
      }
      const refused: Chunk[] = [];
      for (const piece of splitForIsolation(part)) {
        const outcome = await attempt(readerId, piece);
        if ("result" in outcome) {
          progress.accepted = true;
          progress.skipped += await applyResult(
            readerId,
            piece,
            outcome.result,
          );
        } else if (stopsFlush(piece, outcome.error)) {
          return "stopped";
        } else if (refusesTheRows(outcome.error)) {
          refused.push(piece);
        } else {
          return "server";
        }
      }
      for (const piece of refused) {
        const result = await probe(piece);
        if (result !== "done") return result;
      }
      return "done";
    }

    const result = await probe(chunk);
    if (result === "stopped") return STOPPED;
    if (result === "server") {
      backOff(chunk, error, "backoff");
      return STOPPED;
    }
    succeeded();
    return { status: "acked", skipped: progress.skipped };
  }

  async function drain(): Promise<void> {
    const readerId = currentReaderId();
    if (readerId === null || paused || retry.waiting()) return;
    const chunks = chunkForSubmit(await pendingFor(readerId));
    if (chunks.length === 0) return;
    let skipped = 0;
    let acknowledged = false;
    wordsInDoubt = new Set();
    await bumpSubmitEpoch(db);
    try {
      for (const chunk of chunks) {
        const sent = await send(readerId, chunk, true);
        if (sent.status === "stopped") break;
        skipped += sent.skipped;
        acknowledged = true;
      }
    } finally {
      await bumpSubmitEpoch(db);
    }
    if (skipped > 0) logger.event("sync.submit.skipped", { count: skipped });
    if (acknowledged) onAcknowledged?.();
    if (wordsInDoubt.size > 0) {
      onHistoriesInDoubt?.(readerId, [...wordsInDoubt]);
    }
  }

  function exclusive<T>(work: () => Promise<T>): Promise<T> {
    return locks ? locks.request(OUTBOX_LOCK_NAME, work) : work();
  }

  function flush(): Promise<void> {
    if (inFlight) {
      flushAgain = true;
      return inFlight;
    }
    inFlight = (async () => {
      try {
        do {
          flushAgain = false;
          await exclusive(drain);
        } while (flushAgain);
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  async function flushIfOlderThan(ageMs: number): Promise<void> {
    const readerId = currentReaderId();
    if (readerId === null) return;
    const [lower, upper] = readerRange(readerId);
    const oldest = await Promise.all([
      db.outboxRecords
        .where("[readerId+queuedAt]")
        .between(lower, upper)
        .first(),
      db.outboxResponses
        .where("[readerId+queuedAt]")
        .between(lower, upper)
        .first(),
    ]);
    const cutoff = clock.now() - ageMs;
    if (oldest.some((row) => row !== undefined && row.queuedAt <= cutoff)) {
      await flush();
    }
  }

  async function noteReaderSession(readerId: string): Promise<void> {
    await writeMeta(db, readerSessionKey(readerId), clock.now());
  }

  async function expireQueuedRows(): Promise<void> {
    const current = currentReaderId();
    if (current === null) return;
    await noteReaderSession(current);
    const cutoff = clock.now() - QUEUED_ROW_RETENTION_MS;
    const waiting = (row: { readerId: string; queuedAt: number }) =>
      row.readerId !== current && row.queuedAt < cutoff;
    const expired = await db.transaction(
      "rw",
      [
        db.outboxRecords,
        db.outboxResponses,
        db.outboxBookmarks,
        db.fluency,
        db.meta,
      ],
      async () => {
        const [records, responses, bookmarks] = await Promise.all([
          db.outboxRecords.filter(waiting).toArray(),
          db.outboxResponses.filter(waiting).toArray(),
          db.outboxBookmarks.filter(waiting).toArray(),
        ]);
        const candidates = [
          ...new Set(
            [...records, ...responses, ...bookmarks].map((row) => row.readerId),
          ),
        ];
        const sessionAt = await db.meta.bulkGet(
          candidates.map(readerSessionKey),
        );
        // Re-read: a reader who signed in since the run began is not absent.
        const signedIn = currentReaderId();
        const absent = new Set(
          candidates.filter((readerId, i) => {
            const at = sessionAt[i]?.value;
            return (
              readerId !== signedIn && (typeof at !== "number" || at < cutoff)
            );
          }),
        );
        const ofAbsent = <T extends { readerId: string }>(rows: T[]) =>
          rows.filter((row) => absent.has(row.readerId));
        const expiring = {
          records: ofAbsent(records),
          responses: ofAbsent(responses),
          bookmarks: ofAbsent(bookmarks),
        };
        await Promise.all([
          db.outboxRecords.bulkDelete(
            expiring.records.map((row) => row.clientRecordId),
          ),
          db.outboxResponses.bulkDelete(
            expiring.responses.map((row) => row.clientResponseId),
          ),
          db.outboxBookmarks.bulkDelete(
            expiring.bookmarks.map((row): [string, string] => [
              row.readerId,
              row.readingId,
            ]),
          ),
        ]);
        await dropFromHistories(expiring.responses);
        return expiring;
      },
    );
    const readers = [
      ...new Set(
        [...expired.records, ...expired.responses, ...expired.bookmarks].map(
          (row) => row.readerId,
        ),
      ),
    ];
    if (readers.length === 0) return;
    const countFor = (
      rows: readonly { readerId: string }[],
      readerId: string,
    ) => rows.filter((row) => row.readerId === readerId).length;
    logger.error(
      "sync.outbox.expired",
      { type: "OutboxExpired", message: "queued rows expired unsent" },
      {
        records: expired.records.length,
        responses: expired.responses.length,
        bookmarks: expired.bookmarks.length,
        retention_ms: QUEUED_ROW_RETENTION_MS,
        readers: readers.map((readerId) => ({
          reader_id: readerId,
          records: countFor(expired.records, readerId),
          responses: countFor(expired.responses, readerId),
          bookmarks: countFor(expired.bookmarks, readerId),
        })),
      },
    );
  }

  // An expired response was replayed into its word's stored history; left
  // there, the reader's next tap would compute its actionId from a
  // response the server never received.
  async function dropFromHistories(
    expired: readonly OutboxResponseRow[],
  ): Promise<void> {
    const keys = [
      ...new Map(
        expired.map((row): [string, [string, string]] => [
          JSON.stringify([row.readerId, row.word]),
          [row.readerId, row.word],
        ]),
      ).values(),
    ];
    const stored = await db.fluency.bulkGet(keys);
    const rebuilt = await Promise.all(
      stored
        .filter((row): row is FluencyRow => row !== undefined)
        .map(async (row): Promise<FluencyRow> => ({
          ...withServerHistory(
            row.readerId,
            row.word,
            row.serverHistory,
            row.lastUpdated,
            await db.outboxResponses
              .where("[readerId+word]")
              .equals([row.readerId, row.word])
              .toArray(),
          ),
          ...(row.serverMerged && { serverMerged: true }),
        })),
    );
    await db.fluency.bulkPut(rebuilt);
  }

  return {
    queueRecord: (record, responses = [], readerId) =>
      putRecord(record, "pending", responses, readerId),
    holdTapRecord: (provisional, responses = [], readerId) =>
      putRecord(provisional, "provisional", responses, readerId),
    async finalizeHeldTapRecord(final, readerId) {
      await putRecord(final, "pending", [], readerId);
    },
    queueFluencyResponses,
    flush,
    retryNow() {
      paused = false;
      retry.reset();
      return flush();
    },
    flushIfOlderThan,
    noteReaderSession,
    expireQueuedRows,
    whenIdle: () => inFlight ?? Promise.resolve(),
  };
}

const chunkKey = (chunk: Chunk): string =>
  chunk.records[0]?.clientRecordId ??
  chunk.responses[0]?.clientResponseId ??
  "";

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// Only a refusal that the same rows would meet again justifies quarantine:
// an OCC conflict, an overload or a dropped call says nothing about a row,
// and losing good research rows in a flaky period is worse than waiting.
export function refusesTheRows(error: unknown): boolean {
  return (
    error instanceof Error && error.message.includes("ArgumentValidationError")
  );
}

export async function pruneHeldTaps(db: ReaderDb, now: number): Promise<void> {
  const cutoff = now - HELD_TAP_RETENTION_MS;
  await db.meta
    .where("key")
    .startsWith(HELD_TAP_PREFIX)
    .filter((row) => (row.value as HeldTap).heldAt < cutoff)
    .delete();
}

export async function pruneQuarantine(
  db: ReaderDb,
  now: number,
  retentionMs = QUARANTINE_RETENTION_MS,
): Promise<number> {
  const cutoff = now - retentionMs;
  return db.quarantine.filter((row) => row.quarantinedAt < cutoff).delete();
}
